import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSales } from '../hooks/useSales';
import { priceOf, ILS, formatFullDateTime } from '../utils/salesUtils';
import { CATALOG } from '../constants/catalog';
import '../styles/pages/sale-receipt.css';

const STATUS_LABELS = {
  paid: 'PAGADO', partial: 'PAGO PARCIAL', due: 'PENDIENTE',
  draft: 'BORRADOR', cancelled: 'CANCELADA',
};

const METHOD_LABELS = { Cash: 'Efectivo', Card: 'Tarjeta', Bit: 'Bit', Transfer: 'Transferencia', Cashback: 'Cashback' };
const CASHBACK_RATE = 0.01;

export default function SaleReceipt() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { allSales, loading: salesLoading, getSaleById } = useSales();

  const [fetchedSale, setFetchedSale] = useState(null);
  const [loadingSale, setLoadingSale] = useState(true);

  useEffect(() => {
    let isMounted = true;
    setLoadingSale(true);
    if (id) {
      getSaleById(id).then(data => {
        if (isMounted && data) setFetchedSale(data);
        if (isMounted) setLoadingSale(false);
      });
    } else {
      setLoadingSale(false);
    }
    return () => { isMounted = false; };
  }, [id, getSaleById]);

  const sale = fetchedSale || allSales.find(s => s._id === id || s.id === id);

  // Auto-print once the sale is loaded
  useEffect(() => {
    if (!sale) return;
    const t = setTimeout(() => window.print(), 400);
    return () => clearTimeout(t);
  }, [sale]);

  if ((loadingSale || salesLoading) && !sale) {
    return <div className="rcpt-loading">Cargando recibo…</div>;
  }

  if (!sale) {
    return (
      <div className="rcpt-loading">
        Venta no encontrada.{' '}
        <button onClick={() => navigate('/pos')}>Volver</button>
      </div>
    );
  }

  const saleTitle = sale.saleNumber
    ? `#${String(sale.saleNumber).padStart(4, '0')}`
    : `#${(sale._id || sale.id)?.slice(-6).toUpperCase()}`;

  const dogName = sale.dog?.name || '—';
  const ownerName = sale.dog?.owners?.[0]?.name || '';
  const ownerPhone = sale.dog?.owners?.[0]?.phone || '';
  const status = sale.status || 'draft';

  const subtotal = (sale.items || []).reduce((s, it) => s + priceOf(it) * (it.qty || 1), 0);
  const discount = sale.discount || null;
  const discountAmt = sale.discountAmount || 0;
  const total = sale.total || 0;
  const paid = (sale.payments || []).reduce((s, p) => s + (p.amount || 0), 0);
  const remaining = sale.remaining || 0;
  const cashbackEarned = (total * CASHBACK_RATE).toFixed(2);

  const createdByName = sale.createdBy?.username || sale.createdBy?.name || '';

  return (
    <div className="rcpt-wrap">
      {/* Screen-only close button */}
      <button className="rcpt-close no-print" onClick={() => window.close()}>✕ Cerrar</button>

      <div className="rcpt-paper">
        {/* Header */}
        <div className="rcpt-header">
          <div className="rcpt-brand">🐾 Happy Tails</div>
          <div className="rcpt-sub">Recibo de venta</div>
        </div>

        <div className="rcpt-divider" />

        {/* Meta */}
        <div className="rcpt-meta">
          <div className="rcpt-meta-row">
            <span>Venta</span><strong>{saleTitle}</strong>
          </div>
          <div className="rcpt-meta-row">
            <span>Fecha</span><strong>{formatFullDateTime(sale.createdAt)}</strong>
          </div>
          {createdByName && (
            <div className="rcpt-meta-row">
              <span>Atendido por</span><strong>{createdByName}</strong>
            </div>
          )}
        </div>

        <div className="rcpt-divider" />

        {/* Customer */}
        <div className="rcpt-section-title">Cliente</div>
        <div className="rcpt-meta">
          <div className="rcpt-meta-row">
            <span>Perro</span><strong>{dogName}</strong>
          </div>
          {ownerName && (
            <div className="rcpt-meta-row">
              <span>Dueño</span><strong>{ownerName}</strong>
            </div>
          )}
          {ownerPhone && (
            <div className="rcpt-meta-row">
              <span>Teléfono</span><strong>{ownerPhone}</strong>
            </div>
          )}
        </div>

        <div className="rcpt-divider" />

        {/* Items */}
        <div className="rcpt-section-title">Servicios</div>
        <div className="rcpt-items">
          {(sale.items || []).map((it, i) => {
            const unit = priceOf(it);
            const catIcon = CATALOG[it.cat]?.icon || '🛍️';
            return (
              <div key={i} className="rcpt-item">
                <div className="rcpt-item-name">
                  {catIcon} {it.name}
                  {it.sized && it.size ? ` (${it.size})` : ''}
                </div>
                <div className="rcpt-item-right">
                  <span className="rcpt-item-qty">{it.qty || 1} × {ILS(unit)}</span>
                  <strong>{ILS(unit * (it.qty || 1))}</strong>
                </div>
              </div>
            );
          })}
        </div>

        <div className="rcpt-divider" />

        {/* Totals */}
        <div className="rcpt-totals">
          <div className="rcpt-total-row">
            <span>Subtotal</span><span>{ILS(subtotal)}</span>
          </div>
          {discount && discountAmt > 0 && (
            <div className="rcpt-total-row discount">
              <span>
                {discount.type === 'membership'
                  ? '🏅 Membresía · cubierto'
                  : discount.type === 'bath-package'
                  ? '🛁 Paquete de baños'
                  : discount.type === 'percent'
                  ? `Descuento · ${discount.value}%`
                  : 'Descuento'}
              </span>
              <span>− {ILS(discountAmt)}</span>
            </div>
          )}
          <div className="rcpt-total-row grand">
            <span>Total</span><span>{ILS(total)}</span>
          </div>
          {total > 0 && sale.earnCashback !== false && (
            <div className="rcpt-total-row cashback">
              <span>🎉 Cashback ganado · 1%</span>
              <span>+$ {cashbackEarned}</span>
            </div>
          )}
        </div>

        {/* Payments */}
        {(sale.payments || []).length > 0 && (
          <>
            <div className="rcpt-divider" />
            <div className="rcpt-section-title">
              Pagos recibidos ({sale.payments.length})
            </div>
            <div className="rcpt-payments">
              {(sale.payments || []).map((p, i) => (
                <div key={i} className="rcpt-pay-row">
                  <span>{METHOD_LABELS[p.method] || p.method}</span>
                  <strong>{ILS(p.amount)}</strong>
                </div>
              ))}
            </div>
            {remaining > 0 && (
              <div className="rcpt-remaining">
                Saldo pendiente: <strong>{ILS(remaining)}</strong>
              </div>
            )}
          </>
        )}

        <div className="rcpt-divider" />

        {/* Status badge */}
        <div className={`rcpt-status rcpt-status-${status}`}>
          {STATUS_LABELS[status] || status.toUpperCase()}
        </div>

        {/* Footer */}
        <div className="rcpt-footer">
          ¡Gracias por confiar en Happy Tails! 🐾
        </div>
      </div>
    </div>
  );
}
